import {
  HomeownersInsurance,
  IStreetAddress,
  StreetAddress,
  Validate,
} from '@oneethos/shared'
import { AddressInputs, DateInput, EmailInput, PhoneInput, TextInput } from '../../components'
import { TbClipboardList } from "react-icons/tb"

type HomeownersInsuranceFormProps = {
  insurance?: HomeownersInsurance
  installationAddress: string
  onChange: (insurance: HomeownersInsurance) => void
}

const calendarDateIndex = (year: number, month: number, day: number): number => {
  return (year * 10000) + (month * 100) + day
}

const isCalendarDateAfterDay = (dateStr: string, asOf: Date): boolean => {
  const [month, day, year] = dateStr.split('/').map(part => parseInt(part, 10))
  const dateIndex = calendarDateIndex(year, month, day)
  const todayIndex = calendarDateIndex(
    asOf.getFullYear(),
    asOf.getMonth() + 1,
    asOf.getDate(),
  )
  return dateIndex > todayIndex
}

const policyEffectiveDateError = (value: string): string => {
  if (!value.trim()) {
    return 'Policy effective date is required.'
  }

  if (Validate.isValidDate(value)) {
    return 'Policy effective date must be a valid mm/dd/yyyy date.'
  }

  return ''
}

const policyExpirationDateError = (value: string, asOf: Date): string => {
  if (!value.trim()) {
    return 'Policy expiration date is required.'
  }

  if (Validate.isValidDate(value)) {
    return 'Policy expiration date must be a valid mm/dd/yyyy date.'
  }

  if (!isCalendarDateAfterDay(value, asOf)) {
    return 'Policy expiration date must be after today.'
  }

  return ''
}

const agentPhoneError = (phone: string): string => {
  if (!phone.trim()) {
    return 'Agent phone is required.'
  }

  if (Validate.isValidPhone(phone)) {
    return 'Agent phone must be 10 digits, no punctuation.'
  }

  return ''
}

const agentEmailError = (email: string): string => {
  if (!email.trim()) {
    return 'Agent email is required.'
  }

  if (Validate.isValidEmail(email)) {
    return 'Agent email must be a valid email address.'
  }

  return ''
}

export const homeownersInsuranceFieldErrors = (
  insurance: HomeownersInsurance | null | undefined,
  asOf: Date,
): Record<string, string> => {
  const results: Record<string, string> = {}
  const policy = insurance || {}

  if (!policy.companyName?.trim()) {
    results['homeownersInsurance.companyName'] = 'Insurance company name is required.'
  }

  if (!policy.policyNumber?.trim()) {
    results['homeownersInsurance.policyNumber'] = 'Policy number is required.'
  }

  if (!policy.agentName?.trim()) {
    results['homeownersInsurance.agentName'] = 'Agent name is required.'
  }

  if (!policy.coveredAddress || !StreetAddress.isValid(policy.coveredAddress)) {
    results['homeownersInsurance.coveredAddress'] = 'Please enter the full address covered by the policy.'
  } else if (Validate.isValidZip(policy.coveredAddress.zip || '')) {
    results['homeownersInsurance.coveredAddress'] = 'Covered address ZIP code must be 5 digits.'
  }

  const effectiveDateError = policyEffectiveDateError(policy.effectiveDate || '')
  if (effectiveDateError) {
    results['homeownersInsurance.effectiveDate'] = effectiveDateError
  }

  const expirationDateError = policyExpirationDateError(policy.expirationDate || '', asOf)
  if (expirationDateError) {
    results['homeownersInsurance.expirationDate'] = expirationDateError
  }

  const phoneError = agentPhoneError(policy.agentPhone || '')
  if (phoneError) {
    results['homeownersInsurance.agentPhone'] = phoneError
  }

  const emailError = agentEmailError(policy.agentEmail || '')
  if (emailError) {
    results['homeownersInsurance.agentEmail'] = emailError
  }

  return results
}

const CompletedIDVerification = ({ installationAddress }: { installationAddress: string }) => (
  <div className="card my-3" style={{ transform: 'none' }}>
    <div className="card-body ">
      <span className="fw-bold">Property Insurance Details</span>
      <div className="mt-3">
        <div className="d-flex flex-row gap-3">
          <div className="text-primary bg-primary-subtle rounded-circle p-2" >
            <TbClipboardList size={24} />
          </div>
          <p className="text-muted m-0">
          Because you indicated that you have lived at this address for less than 90 days, we
          may contact your insurance company to confirm whether there is an active homeowners
          insurance policy for the property. Please provide the following information regarding
          your homeowners insurance policy for <b>{installationAddress}</b>.
          </p>
        </div>
      </div>
    </div>
  </div>
)

const HomeownersInsuranceForm = ({
  insurance,
  installationAddress,
  onChange,
}: HomeownersInsuranceFormProps) => {
  const policy = insurance || {}

  const updateField = <Field extends keyof HomeownersInsurance>(
    field: Field,
    value: HomeownersInsurance[Field],
  ): void => {
    onChange({
      ...policy,
      [field]: value,
    })
  }

  return (
    <div>
      <CompletedIDVerification installationAddress={installationAddress} />
      <div className="form-group">
        <label className="form-label">
          Insurance Company
        </label>
        <TextInput
          value={policy.companyName || ''}
          onChange={value => updateField('companyName', value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label">
          Policy Number
        </label>
        <TextInput
          value={policy.policyNumber || ''}
          onChange={value => updateField('policyNumber', value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label">
          Agent Name
        </label>
        <TextInput
          value={policy.agentName || ''}
          onChange={value => updateField('agentName', value)}
        />
      </div>
      <AddressInputs
        label="Address covered by the policy"
        address={policy.coveredAddress || {}}
        onChange={(coveredAddress: IStreetAddress) => updateField('coveredAddress', coveredAddress)}
      />
      <div className="form-group">
        <label className="form-label">
          Policy Effective Date
        </label>
        <DateInput
          value={policy.effectiveDate || ''}
          onChange={value => updateField('effectiveDate', value)}
          validator={policyEffectiveDateError}
        />
      </div>
      <div className="form-group">
        <label className="form-label">
          Policy Expiration Date
        </label>
        <DateInput
          value={policy.expirationDate || ''}
          onChange={value => updateField('expirationDate', value)}
          validator={value => policyExpirationDateError(value, new Date())}
        />
      </div>
      <div className="form-group">
        <label className="form-label">
          Agent Phone
        </label>
        <PhoneInput
          value={policy.agentPhone || ''}
          onChange={value => updateField('agentPhone', value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label">
          Agent Email
        </label>
        <EmailInput
          value={policy.agentEmail || ''}
          onChange={value => updateField('agentEmail', value)}
        />
      </div>
    </div>
  )
}

export default HomeownersInsuranceForm
